<script lang="ts">
  import type { PriceItem } from "$lib/types/spotPriser";
  import getPriceToPx, { getPriceToSvg } from "./priceToPx";
  import getAverage from "$lib/utils/getAverage";
  import Bar from "./PriceBarChartBar.svelte";
  import Line from "./ChartYAxisLine.svelte";
  import YAxisTick from "./ChartYAxisTick.svelte";
  import { axisRight as axisY, select } from "d3";
  import ChartInteraction from "./ChartInteraction.svelte";
  import { localeRound } from "$lib/utils/math";
  import { fade, fly } from "svelte/transition";
  import { theTime } from "$lib/utils/the-time";
  import { getHoursOfUTCDateTimeStringFromDateDay } from "$lib/utils/time";

  const chartMinH = 110;
  const priceOut = (price: string | number | undefined) => {
    const priceStr = typeof price !== "number" ? localeRound(+(price || "0")) : localeRound(price);
    return !priceStr || !priceStr.match(/infinity$/i) ? priceStr : "…,…";
  };

  let {
    prices,
    includeStotte = false,
    showRatio = true,
    currentHourIndex,
    title,
    openSettings,
    selectedDate = new Date(),
  }: {
    prices: undefined | PriceItem[];
    includeStotte?: boolean;
    showRatio?: boolean;
    currentHourIndex: number | null;
    title?: import("svelte").Snippet;
    openSettings?: import("svelte").Snippet;
    selectedDate?: Date;
  } = $props();

  // For DTS, an array of hours from recursively getting every hour from selectedDay
  const utcHours = $derived(selectedDate && getHoursOfUTCDateTimeStringFromDateDay(selectedDate));
  let hoveredHour: number | undefined = $state();
  let selectedHour: number | undefined = $state(); // hour index
  let chartWidth: number = $state(0);
  let chartHeight = $state(chartMinH);
  let priceToPx = $derived(prices?.length ? getPriceToPx(prices, chartHeight, showRatio) : null);
  let priceToSvg = $derived(
    prices?.length ? getPriceToSvg(prices, chartHeight + 1, showRatio) : null
  );
  let maxPrice = $derived((prices?.length && Math.max(...prices.map((x) => x.pris))) || 0);
  let minPrice = $derived.by(() => {
    const min = (prices?.length && Math.min(...prices.map((x) => x.pris || 0))) || 0;
    return showRatio ? min : min < 0 ? min : 0;
  });
  let average = $derived(prices?.length && getAverage(prices.map((x) => x.pris)));
  let includesZero = $derived(minPrice <= 0 && priceToPx ? priceToPx(0) : false);

  // y axis
  let gy: SVGGElement;

  $effect(() => {
    if (gy && priceToSvg) {
      select(gy).transition().duration(500).call(axisY(priceToSvg).ticks(5));
    }
  });
</script>

<div
  id="timespris-chart-container"
  class="[--tick-y-left:calc(.5*var(--space-edge-x))] desktop:[--tick-y-left:var(--s-4)] component-container mx-.25edge-x px-1 px-.75edge-x desktop:mx-edge-x desktop:px-8 pt-4 bg-white rounded-1rem shadow-[0_1px_3px_rgba(0,0,0,0.375)] isolate my-3 text-sm overflow-x-hidden"
  style="--chart-height: {chartHeight}px;"
>
  <div
    aria-live="polite"
    class="relative grid grid-cols-[auto_1fr] items-start gap-x-3px landscape:gap-x-3 children-m-0 max-desktop:text-sm pb-4 w-[calc(100%+var(--tick-y-left))] left-[calc(-1*var(--tick-y-left))]"
  >
    <h2 class="text-sm pt-3px">
      {#if title}{@render title()}{:else}Øre/kWh{/if}
    </h2>

    <p class="m-0 flex justify-center gap-x-6px landscape:gap-x-3">
      {#if typeof selectedHour === "number"}
        {@const selectedPrice = prices && prices[selectedHour]?.pris}
        {@const stottePrice = includeStotte && prices && prices[selectedHour]?.hasStromstottePris}

        {#key selectedHour}
          <button
            onclick={() => (selectedHour = undefined)}
            class="button min-w-0 border-0 py-0 px-3px bg-$glod shadow-[0_0_0_3px_var(--aurora)] whitespace-nowrap font-normal"
            in:fly={{ y: -8, duration: 375, delay: 75 }}
          >
            <span class="legend inline-block">
              <span
                class:font-bold={!stottePrice}
                class:text-sm={stottePrice}
                class:line-through={stottePrice}
              >
                {priceOut(selectedPrice ?? undefined)}
              </span>

              {#if stottePrice}
                <strong>
                  {priceOut(stottePrice)}
                </strong>
              {/if}

              {#if typeof selectedHour === "number"}
                kl {`${theTime(utcHours[selectedHour]).to("HH")}`}
              {/if}
            </span>
          </button>
        {/key}
      {/if}

      {#if currentHourIndex}
        {@const currentPrice = prices && prices[currentHourIndex]?.pris}
        {#key currentHourIndex || prices}
          <span
            class="shadow-[0_-3px_var(--lysning)] whitespace-nowrap"
            in:fly|global={{ y: -8, duration: 375, delay: 75 }}
          >
            {#if currentPrice}
              <strong>
                {priceOut(currentPrice ?? 0)}
              </strong>
            {/if}
            nå kl
            {`${theTime(utcHours[currentHourIndex]).to("HH")}`}
          </span>
        {/key}
      {/if}
    </p>
  </div>

  <div
    class="chart-container relative select-none z-1 text-sm mb-8 before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(transparent_calc(87.5%-var(--h-bar-0,0px)),var(--bg)_calc(100%-var(--h-bar-0,0px)))] before:z-2"
    style="height: {chartHeight}px; --_averagePos: {priceToPx &&
      priceToPx(
        average || 0
      )}px; --grid-color:color-mix(in oklab, var(--fg), var(--bg) 87.5%); --h-bar-0:{priceToPx &&
    utcHours?.length &&
    prices?.length
      ? Math.round(priceToPx(prices[0].pris))
      : 0}px"
  >
    <ChartInteraction hours={utcHours} {chartWidth} bind:hoveredHour bind:selectedHour />

    <!-- Top y-axis -->
    <YAxisTick bottom={chartHeight + 1} class="leading-tight">
      {#key maxPrice}
        <small in:fade class="relative bottom-4px"
          ><strong>{priceOut(maxPrice)}</strong> dyrest</small
        >
      {/key}
    </YAxisTick>
    <Line color="grid-color z-4" style="solid" bottom={chartHeight + 1} />
    <!-- 0 y-axis tick -->
    {#if minPrice < 0 && priceToPx}
      <YAxisTick bottom={priceToPx(0)} class="leading-tight" color="geitrams">
        {#key maxPrice}
          <small in:fade class="relative bottom-1px text-$solbar text-.5em landscape:text-.75em"
            >0</small
          >
        {/key}
      </YAxisTick>
    {/if}
    <!-- Bottom y-axis -->
    <YAxisTick bottom={0} class="leading-tight">
      {#if showRatio}
        <small class="absolute top-3px"
          ><strong>{priceOut(minPrice)}</strong> <span class="block">billigst</span></small
        >
      {:else}
        <small class="absolute top-4px"
          ><strong>{minPrice > 0 ? 0 : priceOut(minPrice)}</strong></small
        >
      {/if}
    </YAxisTick>
    <Line color="grid-color" style="solid" bottom={0} />

    <svg
      width={chartWidth}
      height={chartHeight + 1}
      viewBox="0 0 {chartWidth} {chartHeight + 1}"
      class="axis-svg absolute inset-0"
    >
      <g bind:this={gy} class="translate-x--3px"></g>
    </svg>

    <ul
      bind:clientWidth={chartWidth}
      bind:clientHeight={chartHeight}
      style="height: clamp({chartMinH}px, 15vmin, {chartMinH *
        1.75}px); grid-template-columns: repeat({utcHours.length}, minmax(0,1fr));"
      class="chart gap-x-1px list-none h-full p-0 grid m-0 leading-none"
    >
      {#each utcHours as hour, i (hour)}
        {@const hh = theTime(hour).to("HH")}
        {@const item = prices && prices[i]}
        {@const priceNumber = item?.pris}
        {@const price = priceOut(priceNumber || 0)}
        {@const hPx = item && priceToPx ? priceToPx(priceNumber || 0) : 0}
        {@const visibleLabel = (i + 1) % 4 === 0}
        {@const hPxStotte =
          includeStotte && item?.hasStromstottePris
            ? item.hasStromstottePris >= minPrice && priceToPx
              ? priceToPx(item.hasStromstottePris)
              : 1
            : undefined}
        {@const notHovered =
          typeof hoveredHour !== "number" || (typeof hoveredHour === "number" && i !== hoveredHour)}

        <li
          class:current={currentHourIndex && hh === theTime(utcHours[currentHourIndex]).to("HH")}
          class:selected={typeof selectedHour === "number" && i === selectedHour}
          class:hovered={!notHovered}
          class="inline-block w-full relative h-full text-sm max-w-full"
        >
          <small
            class="pl-2px absolute left-0 right-0 top-[calc(100%+4px)] block whitespace-nowrap leading-tight text-[--fg-muted]"
          >
            <span
              class:max-laptop:sr-only={!visibleLabel}
              class:sr-only={i === 0}
              style="word-spacing:-.125ch"
            >
              <strong class="sr-only">{price}</strong>
              <span class="text-sm">kl</span> <strong>{hh}</strong>
            </span>
            <span
              class="absolute top--3px left--1px h-.75em border-l-1px border-l-solid"
              class:max-laptop:h-3px={!visibleLabel}
              class:h-3px={i === 0}
            ></span>
          </small>

          {#if !notHovered}
            <span
              class="hidden absolute z-100 top--8 w-full justify-center"
              class:laptop:flex={!notHovered}
            >
              <span
                class="hovered-hour-info button min-w-0 border-0 py-1 px-2 bg-[--primary-light-80] button-drop-shadow font-normal whitespace-nowrap min-w-fit backdrop-blur-1px"
                in:fly|global={{ y: -8, duration: 375, delay: 75 }}
                out:fade={{ duration: 62.5 }}
              >
                <strong class="whitespace-nowrap">{price}</strong>
                {#if !notHovered}
                  <span class="whitespace-nowrap">kl {hh}</span>
                {/if}
              </span>
            </span>
          {/if}

          <span class="block absolute inset-0" style="height: {chartHeight}px">
            <Bar {hPx} {chartHeight} {hPxStotte} {includesZero} />
          </span>

          {#if i === 0}
            <span
              class="absolute h-[calc(100%+2px)] border-l-solid border-l-1px top-0 left--1px z-3"
            ></span>
          {/if}
        </li>
      {/each}
    </ul>
  </div>

  {#if openSettings}
    <div class="relative left-[calc(-1*var(--tick-y-left))]">
      {@render openSettings?.()}
    </div>
  {/if}
</div>

<style lang="postcss">
  .component-container {
    --max-bg: color-mix(in oklab, var(--naust), transparent 25%);
    --min-bg: color-mix(in oklab, var(--skog), transparent 25%);
    --average-color: var(--fg-muted);
    --stotte-hard-s: 3px;
  }

  .component-container {
    --bar-color: color-mix(in oklab, var(--geitrams), var(--bg) 20%);
    --img-stotted: repeating-linear-gradient(
      58deg,
      color-mix(in oklab, var(--bar-color) 62.5%, var(--bg)),
      color-mix(in oklab, var(--bar-color) 62.5%, var(--bg)) var(--stotte-hard-s),
      color-mix(in oklab, var(--bar-color) 25%, var(--bg)) var(--stotte-hard-s),
      color-mix(in oklab, var(--bar-color) 25%, var(--bg)) calc(var(--stotte-hard-s) * 2)
    );
  }

  .component-container :global(.chart-interaction) {
    & :global(~ .chart li:is(.selected, .hovered)) {
      --bar-color: var(--geitrams);
    }
  }

  ul.chart {
    :where(:global(li))::before,
    :where(:global(li))::after {
      content: "";
      position: absolute;
      z-index: 3;
    }

    :where(:global(li))::after {
      --_h: 6px;

      left: 50%;
      border: var(--_h) solid transparent;
      border-block-start-color: var(--pointer-color, var(--glod));
      transition: opacity 0.125s var(--easing);
      width: var(--_h);
      opacity: 0;
      /* pos */
      top: calc(-1 * var(--_h) - 3px);
      transform: translateX(calc(var(--_h) * -1));
    }

    :where(:global(li))::before {
      background-image: repeating-linear-gradient(
        -58deg,
        var(--line-color, var(--pointer-color)),
        var(--line-color, var(--pointer-color)) 1px,
        rgba(0, 0, 0, 0) 1px,
        rgba(0, 0, 0, 0) 4px
      );
      height: 100%;
      width: 100%;
      opacity: 0;
      transition: opacity 0.125s var(--easing);
      /* pos */
      top: 2px;
      left: 0;
    }
  }

  li:is(.current, .selected) {
    &::before,
    &::after {
      opacity: 1;
    }
  }

  li.current {
    --pointer-color: var(--lysning);
    --line-color: color-mix(in oklab, var(--lysning), transparent 87.5%);
  }

  li.selected {
    --pointer-color: var(--geitrams);
    --line-color: color-mix(in oklab, var(--glod), transparent 50%);
  }

  @media (--laptop) {
    ul.chart {
      li.hovered {
        --pointer-color: color-mix(in oklab, var(--isbre), transparent 12.5%);
        position: relative;

        &::before {
          opacity: 1;
        }
      }
    }
  }
</style>
